import { Checkbox } from "@heroui/react";
import { XIcon } from "@phosphor-icons/react";

import { TodoItem } from "../types";
import { getTodoCheckboxClasses, getTodoDueDateClasses } from "../utils/classes";
import { formatDueDate } from "@/shared/utils/date";
import clsx from "clsx";

interface TodoListItemProps {
  item: TodoItem;
  onToggleDone?: (id: number) => void;
  isReadOnly?: boolean;
  showDue?: boolean;
  onPress?: (id: number) => void;
  onRemove?: (id: number) => void;
}

export function TodoListItem({ item, onToggleDone, isReadOnly, showDue, onPress, onRemove }: TodoListItemProps) {
  const formattedDueDate = item.dueDate ? formatDueDate(item.dueDate) : null;

  const isOverdue = (() => {
    if (!item.dueDate || item.done) return false;
    const endOfDueDay = new Date(item.dueDate);
    endOfDueDay.setHours(23, 59, 59, 999);
    return endOfDueDay < new Date();
  })();
  const showDueDate = (showDue || isOverdue) && item.dueDate;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && onPress) {
      e.preventDefault();
      onPress(item.id);
    }
  };

  return (
    <div className="group relative box-border flex items-center px-4 py-1.5">
      <div className="flex items-center gap-3.5">
        <Checkbox
          classNames={getTodoCheckboxClasses(item.priority)}
          isSelected={item.done}
          className="p-0"
          isDisabled={isReadOnly}
          onValueChange={() => onToggleDone?.(item.id)}
        />
      </div>

      <div className="ml-2 flex w-full min-w-0 flex-1 flex-col">
        <div
          aria-label={onPress ? "Edit todo" : undefined}
          className={clsx(
            "max-w-full transition-transform duration-300 ease-in-out focus-visible:rounded focus-visible:ring-2 focus-visible:outline-none",
            {
              "cursor-pointer": Boolean(onPress),
            }
          )}
          role={onPress ? "button" : undefined}
          tabIndex={onPress ? 0 : undefined}
          onClick={() => onPress?.(item.id)}
          onKeyDown={handleKeyDown}
        >
          <span
            className={`block max-w-full overflow-hidden text-ellipsis whitespace-nowrap ${item.done ? "text-default-400 line-through" : "text-default-500"}`}
          >
            {item.text}
          </span>
        </div>
        {showDueDate && (
          <div
            className={clsx("overflow-hidden transition-all duration-300 ease-in-out", {
              "max-h-6 opacity-100": item.dueDate && !item.done,
              "max-h-0 opacity-0": !item.dueDate || item.done,
            })}
          >
            <div className={getTodoDueDateClasses(formattedDueDate)}>{formattedDueDate}</div>
          </div>
        )}
      </div>

      {onRemove && !item.done && (
        <button
          onClick={() => onRemove(item.id)}
          aria-label="Remove todo"
          className={clsx(
            "cursor-pointer opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:opacity-70"
          )}
        >
          <XIcon />
        </button>
      )}
    </div>
  );
}
